<template>
  <form class="contact-form" @submit.prevent="onSubmit" novalidate>
    <!-- Honeypot -->
    <div class="hp" aria-hidden="true">
      <label>
        Company
        <input v-model="company" type="text" name="company" tabindex="-1" autocomplete="off" />
      </label>
    </div>

    <div class="grid gap-4 sm:grid-cols-2">
      <label class="field">
        <span class="label">Name</span>
        <input
          v-model="name"
          type="text"
          name="name"
          autocomplete="name"
          required
          maxlength="120"
          placeholder="Your name"
          :disabled="status === 'sending'"
        />
      </label>
      <label class="field">
        <span class="label">Email</span>
        <input
          v-model="email"
          type="email"
          name="email"
          autocomplete="email"
          required
          maxlength="200"
          placeholder="you@example.com"
          :disabled="status === 'sending'"
        />
      </label>
    </div>

    <label class="field mt-4">
      <span class="label">Topic</span>
      <select v-model="topic" name="topic" :disabled="status === 'sending'">
        <option>General</option>
        <option>Business / Partnership</option>
        <option>Press</option>
        <option>Support</option>
        <option>Other</option>
      </select>
    </label>

    <label class="field mt-4">
      <span class="label">Message</span>
      <textarea
        v-model="message"
        name="message"
        required
        minlength="10"
        maxlength="5000"
        rows="5"
        placeholder="Tell us about your project, feedback, or question…"
        :disabled="status === 'sending'"
      />
    </label>

    <div class="mt-5 flex flex-wrap items-center gap-3">
      <button type="submit" class="btn-primary" :disabled="status === 'sending'">
        <span v-if="status === 'sending'">Sending…</span>
        <span v-else>Send message</span>
      </button>
      <p v-if="status === 'success'" class="status ok" role="status">
        Thanks — we’ll get back to you soon.
      </p>
      <p v-else-if="status === 'error'" class="status err" role="alert">
        {{ errorText }}
      </p>
    </div>
  </form>
</template>

<script setup>
import { ref } from 'vue'

const name = ref('')
const email = ref('')
const message = ref('')
const topic = ref('General')
const company = ref('')
const status = ref('idle') // idle | sending | success | error
const errorText = ref('')

const ERROR_MAP = {
  invalid_name: 'Please enter your name.',
  invalid_email: 'Please enter a valid email.',
  invalid_message: 'Message should be at least 10 characters.',
  rate_limited: 'Too many messages — try again in a minute.',
  email_not_configured: 'Contact is temporarily unavailable.',
  send_failed: 'Could not send — please email team@balconygames.com.',
  network: 'Network error — check your connection and try again.',
}

async function onSubmit() {
  if (status.value === 'sending') return
  status.value = 'sending'
  errorText.value = ''

  try {
    const res = await fetch('/api/contact', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: name.value,
        email: email.value,
        message: message.value,
        topic: topic.value,
        company: company.value,
      }),
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok || !data.ok) {
      status.value = 'error'
      errorText.value = ERROR_MAP[data.error] || ERROR_MAP.send_failed
      return
    }
    status.value = 'success'
    name.value = ''
    email.value = ''
    message.value = ''
    topic.value = 'General'
  } catch {
    status.value = 'error'
    errorText.value = ERROR_MAP.network
  }
}
</script>

<style scoped>
.contact-form {
  width: 100%;
}

.hp {
  position: absolute;
  left: -10000px;
  width: 1px;
  height: 1px;
  overflow: hidden;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.label {
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--text-muted);
}

input,
select,
textarea {
  width: 100%;
  border-radius: 0.75rem;
  border: 1px solid var(--surface-border);
  background: rgba(0, 0, 0, 0.35);
  color: var(--text);
  padding: 0.75rem 0.9rem;
  font: inherit;
  outline: none;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;
}

input::placeholder,
textarea::placeholder {
  color: color-mix(in srgb, var(--text-muted) 80%, transparent);
}

input:focus,
select:focus,
textarea:focus {
  border-color: color-mix(in srgb, var(--accent) 55%, var(--surface-border));
  box-shadow: 0 0 0 3px var(--accent-soft);
}

input:disabled,
select:disabled,
textarea:disabled {
  opacity: 0.6;
}

.btn-primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 999px;
  background: var(--accent);
  color: var(--accent-on-dark-text);
  font-weight: 650;
  font-size: 0.95rem;
  padding: 0.7rem 1.35rem;
  cursor: pointer;
  transition:
    transform 0.15s ease,
    filter 0.15s ease,
    opacity 0.15s ease;
}

.btn-primary:hover:not(:disabled) {
  filter: brightness(1.06);
  transform: translateY(-1px);
}

.btn-primary:disabled {
  opacity: 0.65;
  cursor: wait;
}

.status {
  font-size: 0.875rem;
}

.status.ok {
  color: #6ee7b7;
}

.status.err {
  color: #fca5a5;
}
</style>
